import type { ReactNode } from 'react';
import { ManualView } from '@/components/ManualView';
import { PAYMENT_PHASES, PAYMENT_STEPS, type PaymentStep } from '@/payment/payment-steps';
import { ROUND_CARD, TONE_STYLES } from '@/payment/payment-styles';

/**
 * PaymentStepList の props。
 */
export interface PaymentStepListProps {
  /** ステップの右に置くボタン */
  renderAction: (step: PaymentStep) => ReactNode;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払準備の 8 ステップを、レポート・証憑・支払の 3 つのまとまりに分けて縦の線でつないで並べる。番号の丸と札の色で、実行・確認・freee のどれかが分かる
 * @graph-connects none
 */
export function PaymentStepList({ renderAction }: PaymentStepListProps) {
  return (
    <div className="flex flex-col gap-5">
      <ul className="m-0 flex list-none flex-wrap gap-3 p-0 text-xs text-[var(--color-text-secondary)]">
        {Object.values(TONE_STYLES).map((tone) => (
          <li key={tone.label} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${tone.dot}`} />
            {tone.label}
          </li>
        ))}
      </ul>

      {PAYMENT_PHASES.map((phase) => (
        <section key={phase} aria-label={phase}>
          <h2 className="m-0 mb-2 text-xs font-semibold tracking-wide text-[var(--color-text-muted)]">
            {phase}
          </h2>
          <ol className="relative m-0 list-none space-y-3 p-0 before:absolute before:top-4 before:bottom-4 before:left-[15px] before:w-px before:bg-[var(--color-border-subtle)]">
            {PAYMENT_STEPS.filter((step) => step.phase === phase).map((step) => {
              const tone = TONE_STYLES[step.tone];
              return (
                <li key={step.no} className="relative flex items-start gap-3">
                  <span
                    className={`relative z-[1] mt-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${tone.dot}`}
                  >
                    {step.no}
                  </span>
                  <div className={`${ROUND_CARD} flex min-w-0 flex-1 items-start gap-4 px-5 py-4`}>
                    <div className="min-w-0 flex-1">
                      <h3 className="m-0 flex flex-wrap items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                        <span
                          className={`rounded-full border px-2 py-px text-xs font-medium ${tone.chip}`}
                        >
                          {tone.label}
                        </span>
                        {step.title}
                      </h3>
                      <p className="mt-1 mb-0 text-sm text-[var(--color-text-secondary)]">
                        {step.description}
                      </p>
                      <details className="mt-1.5">
                        <summary className="cursor-pointer text-xs text-[var(--color-accent)]">
                          詳しい手順・注意点
                        </summary>
                        <ManualView
                          manual={step.detail}
                          className="mt-2 rounded-xl bg-[var(--color-bg-subtle)] px-4 py-2"
                        />
                      </details>
                    </div>
                    {renderAction(step)}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
